import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { ArrowRight, User, Building2, HeartHandshake, Check } from "lucide-react";

const PATHS = [
  {
    id: "individuals", to: "/individuals", icon: User, color: "#2C6BAC", bg: "#E8F0F9",
    title: "Individuals", tag: "Me, my work, my place",
    body: "Founders, remote workers, students, parents, anyone whose day gets interrupted and who wants a calm way back.",
    expect: ["Nine real-world use cases, with the research behind each", "How the refocus card and hotkeys work for one person", "Why your data stays yours, even on a team plan"],
    cta: "I'm an individual",
  },
  {
    id: "enterprise", to: "/enterprise", icon: Building2, color: "#1A3A5C", bg: "#E8ECF2",
    title: "Enterprise", tag: "Teams, departments, the whole org",
    body: "Leaders and People teams who want an honest, anonymised picture of where focus is won and lost across the business.",
    expect: ["Roll-up insights, reporting and governance features", "How clients use aggregate signal to make decisions", "The £500K+ figure and a calculator for your own numbers"],
    cta: "We're an organisation",
  },
  {
    id: "charities", to: "/charities", icon: HeartHandshake, color: "#BA7517", bg: "#FFF4E0",
    title: "Charities", tag: "Inclusion, autonomy, independence",
    body: "Organisations supporting neurodivergent workers, people recovering from burnout, new parents and anyone navigating brain fog.",
    expect: ["Spotlights on the communities WYLO was built for", "Charity pricing and onboarding help for support workers", "A promise: people get their place back, nobody gets watched"],
    cta: "We're a charity",
  },
];

export default function Users() {
  return (
    <MarketingLayout>
      <section className="relative overflow-hidden pt-28 pb-14" data-testid="users-hero">
        <div className="hero-blob" style={{ top: -300, left: -200, opacity: 0.35 }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Using WYLO</p>
          <h1 className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95]">
            Who are you<br /><span className="text-[#1A3A5C]">coming back as?</span>
          </h1>
          <p className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl mx-auto">
            WYLO brings people back to where they left off. How it does that, and what it shows, depends on who you are.
            Pick your path and we'll take you straight there.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {PATHS.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.5 }}
              whileHover={{ y: -8 }} data-testid={`users-path-${p.id}`}
              className="relative rounded-2xl border border-[#E8ECF2] bg-white p-8 flex flex-col overflow-hidden">
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full" style={{ backgroundColor: p.bg }} />
              <div className="relative h-14 w-14 rounded-xl text-white flex items-center justify-center" style={{ backgroundColor: p.color }}><p.icon size={26} /></div>
              <p className="relative mt-6 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: p.color }}>{p.tag}</p>
              <h2 className="relative font-display text-3xl font-bold text-[#1A1A2E] mt-1">{p.title}</h2>
              <p className="relative mt-3 text-sm text-[#555568] leading-relaxed">{p.body}</p>
              <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#555568] font-semibold">What to expect</p>
              <ul className="mt-2 space-y-2 flex-1">
                {p.expect.map((e) => (
                  <li key={e} className="text-sm text-[#1A1A2E] flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: p.color }} /> {e}</li>
                ))}
              </ul>
              <Link to={p.to} className="mt-8" data-testid={`users-cta-${p.id}`}>
                <Button className="w-full h-11 text-white hover:opacity-90" style={{ backgroundColor: p.color }}>{p.cta} <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-[#555568]">
          Not sure? Start with <Link to="/features" className="text-[#2C6BAC] font-semibold hover:underline" data-testid="users-features-link">the features</Link>, or just <Link to="/register" className="text-[#2C6BAC] font-semibold hover:underline">try it free</Link>.
        </p>
      </section>
    </MarketingLayout>
  );
}
